import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
} from '@react-pdf/renderer'
import { FC } from 'react'
import SvgLogo from './icons/logo'
import { Issue } from './issue'
import { Severities } from './severities'
import {
    buildPdfSubtitle,
    createChunks,
    getVisualTodayDate,
} from './utils/utils'
import GTAmericaBold from '@/assets/fonts/GT-America-Standard-Bold.ttf'
import GTAmerica from '@/assets/fonts/GT-America-Standard-Regular.ttf'
import GTAmericaThin from '@/assets/fonts/GT-America-Standard-Thin.ttf'
import GTPressuraThin from '@/assets/fonts/GT-Pressura-Mono-Light.ttf'

const ISSUES_PER_PAGE = 10
const PAGE_SIZE = 'A4'

type PdfIssuesProps = {
    issues: TIssue[]
    onDocUpdated: () => void
    isFiltered: boolean
    filteredTypes: string[]
    statuses: TIssueStatus[]
}

export const PdfIssues: FC<PdfIssuesProps> = ({
    issues,
    onDocUpdated,
    filteredTypes,
    isFiltered,
    statuses,
}) => {
    const visualDate = getVisualTodayDate()
    const subtitle = buildPdfSubtitle(
        isFiltered,
        filteredTypes,
        statuses as TIssueStatus[]
    )

    const issueChunks = createChunks(issues, ISSUES_PER_PAGE)

    return (
        <Document onRender={onDocUpdated}>
            {issueChunks.map((chunk, pageIndex) => (
                <Page key={pageIndex} size={PAGE_SIZE} style={styles.page}>
                    <View style={styles.header} fixed>
                        <SvgLogo />
                    </View>
                    {pageIndex === 0 && (
                        <>
                            <Text style={styles.title}>
                                {'At-Bay Stance - Security Issues '}
                                <Text style={styles.subtitle}>
                                    {visualDate}
                                </Text>
                            </Text>
                            <Text style={styles.subtitle}>{subtitle}</Text>
                        </>
                    )}
                    <View style={styles.content}>
                        <View style={styles.left}>
                            {chunk.map((issue) => (
                                <Issue key={issue.id} issue={issue} />
                            ))}
                        </View>
                        <View fixed style={styles.right}>
                            <Severities />
                        </View>
                    </View>
                </Page>
            ))}
        </Document>
    )
}

const styles = StyleSheet.create({
    page: {
        fontFamily: 'GT-America',
        fontWeight: 'thin',
        position: 'relative',
        padding: '20px 45px',
        flexDirection: 'column',
        backgroundColor: 'white',
        lineHeight: 1.3,
        marginBottom: 40,
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        height: 50,
    },
    logo: {
        height: 40,
    },
    title: {
        fontSize: 23.25,
        fontWeight: 'bold',
        textAlign: 'left',
        marginBottom: 10,
    },
    subtitle: {
        textTransform: 'capitalize',
        fontSize: 12,
        fontWeight: 'normal',
        textAlign: 'left',
        marginBottom: 30,
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 20,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontFamily: 'GT-Pressura',
        position: 'absolute',
        bottom: 30,
        color: '#696969',
        fontWeight: 'thin',
        fontSize: 9.75,
        textAlign: 'right',
        alignSelf: 'center',
        width: '100%',
    },
    left: {
        display: 'flex',
        flex: 3,
    },
    right: {
        flex: 1,
    },
})

Font.register({
    family: 'GT-America',
    fonts: [
        { src: GTAmerica },
        { src: GTAmericaBold, fontWeight: 'bold' },
        { src: GTAmericaThin, fontWeight: 'thin' },
    ],
})

Font.register({
    family: 'GT-Pressura',
    fonts: [{ src: GTPressuraThin, fontWeight: 'thin' }],
})

Font.registerHyphenationCallback((word) => [word])
